/**
 * TxnDrawer.jsx — Phase 3
 *
 * Right-side detail drawer for a single transaction. Replaces the legacy
 * edit modal for the VIEW + QUICK-EDIT path. The create-new modal in
 * pages/Transactions.js stays as-is (it owns all the receipt-scan +
 * category auto-guess + promote-to-recurring logic which is heavy to
 * duplicate).
 *
 * Drawer sections
 * ---------------
 *   Header  — amount (large tabular) + type indicator + close
 *   Meta    — date, account, provenance chip, confidence pill
 *   Recurring — matched recurring commitment (if any) + "View" link
 *   Quick edit — inline editable: description, category, amount, date, notes
 *   Footer  — Save changes (primary), Mark reviewed (secondary), Delete (danger)
 *
 * State model
 * -----------
 * The drawer owns its own `draft` object seeded from the incoming `txn`.
 * When the user presses "Save changes" we call `onSave(draft)` and let
 * the parent decide how to persist (patch supabase + refresh list).
 * This keeps mutation logic in one place (Transactions.js:save()).
 */
import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/CurrencyInput';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CategorySelect } from '@/components/CategorySelect';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatMoney, currencySymbol } from '@/lib/format';
import { prettifyDescription } from '@/lib/txDescription';
import {
  Loader2, Trash2, CheckCircle2, Repeat, ArrowUpRight, ArrowDownRight,
  ArrowLeftRight, TrendingUp, RotateCcw, FileText, Camera, Pencil, ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { provenanceOf, confidenceOf, accountTint, accountInitials } from './txnHelpers';

const PROV_ICONS = { statement: FileText, receipt: Camera, recurring: Repeat, manual: Pencil };
const TYPE_META = {
  expense:    { label: 'Expense',    Icon: ArrowUpRight,    sign: '−' },
  income:     { label: 'Income',     Icon: ArrowDownRight,  sign: '+' },
  investment: { label: 'Investment', Icon: TrendingUp,      sign: '↑' },
  transfer:   { label: 'Transfer',   Icon: ArrowLeftRight,  sign: '⇄' },
  refund:     { label: 'Refund',     Icon: RotateCcw,       sign: '↺' },
};
const CONF_STYLES = {
  high:   { cls: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-400' },
  medium: { cls: 'bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-400' },
  low:    { cls: 'bg-amber-500/10 text-amber-700 border-amber-500/40 dark:text-amber-400' },
};

function makeDraft(txn) {
  if (!txn) return null;
  return {
    id: txn.id,
    amount: String(Math.abs(Number(txn.amount) || 0)),
    type: txn.type || 'expense',
    category: txn.category || 'Miscellaneous',
    description: txn.description || '',
    txn_date: (txn.txn_date || '').slice(0, 10),
    account_id: txn.account_id || '',
    note: txn.note || '',
    recurring: !!txn.recurring,
  };
}

export default function TxnDrawer({
  txn, open, onOpenChange,
  accountsById = new Map(),
  accountsList = [],
  currency = 'INR',
  matchedRecurring = null,   // {id, name, frequency, next_due} or null
  onSave,                     // async (draftPatch) => void
  onDelete,                   // async () => void
  onMarkReviewed,             // async () => void
  onUnlinkRecurring,          // async () => void
  onLinkRecurring,            // async (recurring_id) => void; parent renders picker
  onOpenAdvancedEditor,       // () => void; opens the legacy edit modal for advanced fields
  recurringOptions = [],
}) {
  const [draft, setDraft] = useState(() => makeDraft(txn));
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);

  // Reseed the draft whenever the parent swaps the txn (e.g. clicking a
  // different row while the drawer is open).
  useEffect(() => { setDraft(makeDraft(txn)); }, [txn]);

  if (!txn || !draft) return null;

  const prov = provenanceOf(txn);
  const conf = confidenceOf(txn);
  const ProvIcon = PROV_ICONS[prov.key] || Pencil;
  const typeMeta = TYPE_META[txn.type] || TYPE_META.expense;
  const account = accountsById?.get?.(txn.account_id) || null;
  const accountName = account?.name || txn.account || 'No account';
  const tint = accountTint(txn.account_id);
  const initials = accountInitials(accountName);

  const isDirty = draft && txn && (
    Number(draft.amount) !== Math.abs(Number(txn.amount) || 0) ||
    draft.type !== txn.type ||
    draft.category !== (txn.category || 'Miscellaneous') ||
    draft.description !== (txn.description || '') ||
    draft.txn_date !== (txn.txn_date || '').slice(0, 10) ||
    draft.account_id !== (txn.account_id || '') ||
    draft.note !== (txn.note || '') ||
    !!draft.recurring !== !!txn.recurring
  );

  const handleSave = async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      await onSave?.(draft);
    } finally {
      setSaving(false);
    }
  };
  const handleDelete = async () => {
    setDeleting(true);
    try { await onDelete?.(); }
    finally { setDeleting(false); setConfirmDelete(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
        data-testid="transaction-detail-drawer"
      >
        <SheetHeader>
          <SheetTitle className="text-base">Transaction</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-5">
          {/* Header: account badge + amount + type */}
          <div className="flex items-start gap-3">
            <div
              className="h-11 w-11 rounded-xl grid place-items-center text-[13px] font-semibold shrink-0"
              style={{ background: tint.bg, color: tint.fg }}
              aria-label={accountName}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs text-muted-foreground truncate">{accountName}</div>
              <div className={`font-display text-2xl font-semibold tabular-nums ${
                txn.type === 'income' ? 'text-[hsl(var(--chart-5))]' : ''
              }`} data-testid="transaction-drawer-amount">
                {typeMeta.sign} {formatMoney(Math.abs(Number(txn.amount)), currency)}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 truncate" title={prettifyDescription(txn.description)?.raw || ''}>
                {prettifyDescription(txn.description)?.display || txn.description || '—'}
              </div>
            </div>
          </div>

          {/* Meta chips row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge variant="outline" className="rounded-full text-[10px] flex items-center gap-1 px-2 py-0.5">
              <typeMeta.Icon className="h-3 w-3" />
              {typeMeta.label}
            </Badge>
            <Badge variant="outline" className="rounded-full text-[10px] flex items-center gap-1 px-2 py-0.5" data-testid="transaction-drawer-provenance">
              <ProvIcon className="h-3 w-3" />
              {prov.label.split(' ').slice(0, 3).join(' ')}
            </Badge>
            <Badge
              variant="outline"
              className={`rounded-full text-[10px] px-2 py-0.5 ${CONF_STYLES[conf.key]?.cls || ''}`}
              data-testid="transaction-drawer-confidence-pill"
              title={conf.label}
            >
              {conf.key === 'high' ? 'High' : conf.key === 'medium' ? 'Medium' : 'Low'} confidence
            </Badge>
          </div>

          {/* Matched recurring commitment */}
          {matchedRecurring && (
            <div className="rounded-xl border border-border/60 bg-accent/40 px-3 py-2.5 flex items-center justify-between gap-3" data-testid="transaction-drawer-recurring-match">
              <div className="min-w-0 flex items-center gap-2">
                <Repeat className="h-4 w-4 text-primary shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{matchedRecurring.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {matchedRecurring.frequency || 'monthly'}
                    {matchedRecurring.next_due ? ` · next ${matchedRecurring.next_due}` : ''}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Link to={`/recurring?focus=${matchedRecurring.id}`}>
                  <Button variant="ghost" size="sm" className="rounded-lg" data-testid="transaction-drawer-view-recurring">
                    View <ExternalLink className="h-3 w-3 ml-1" />
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" className="rounded-lg text-muted-foreground" onClick={onUnlinkRecurring} data-testid="transaction-drawer-unlink-recurring">
                  Unlink
                </Button>
              </div>
            </div>
          )}
          {!matchedRecurring && recurringOptions.length > 0 && (
            <div className="rounded-xl border border-dashed border-border/60 px-3 py-2 flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">Not linked to a recurring commitment.</span>
              <Button
                variant="ghost" size="sm" className="rounded-lg"
                onClick={() => setLinkPickerOpen((v) => !v)}
                data-testid="transaction-drawer-link-recurring-toggle"
              >
                Link
              </Button>
            </div>
          )}
          {linkPickerOpen && !matchedRecurring && (
            <div className="rounded-xl border border-border/60 p-2 max-h-48 overflow-y-auto">
              {recurringOptions.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={async () => { setLinkPickerOpen(false); await onLinkRecurring?.(r.id); }}
                  className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-accent text-sm truncate"
                  data-testid={`transaction-drawer-link-option-${r.id}`}
                >
                  {r.name}
                </button>
              ))}
            </div>
          )}

          {/* Quick-edit inline fields */}
          <div className="space-y-3 pt-1 border-t border-border/40">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-medium">Quick edit</div>

            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Input
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                className="rounded-xl"
                data-testid="transaction-drawer-edit-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Amount</Label>
                {/* Phase v3.4.1 — refactored to <CurrencyInput> primitive,
                    which handles dynamic left-padding for 1-3 char symbols
                    (`₹`, `$`, `AED`, `USD`) uniformly across the app. */}
                <CurrencyInput
                  currency={currency}
                  symbolClassName="text-xs left-2.5"
                  min="0"
                  value={draft.amount}
                  onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                  data-testid="transaction-drawer-edit-amount"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Date</Label>
                <Input
                  type="date"
                  value={draft.txn_date}
                  onChange={(e) => setDraft({ ...draft, txn_date: e.target.value })}
                  className="rounded-xl"
                  data-testid="transaction-drawer-edit-date"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <CategorySelect
                type={draft.type}
                value={draft.category}
                onChange={(v) => setDraft({ ...draft, category: v })}
                testId="transaction-drawer-edit-category"
              />
            </div>

            {accountsList.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs">Account</Label>
                <Select
                  value={draft.account_id || undefined}
                  onValueChange={(v) => setDraft({ ...draft, account_id: v })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="transaction-drawer-edit-account">
                    <SelectValue placeholder="Pick an account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accountsList.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">Note (optional)</Label>
              <Input
                value={draft.note || ''}
                onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                maxLength={200}
                placeholder="e.g. shared dinner, split with a friend"
                className="rounded-xl"
                data-testid="transaction-drawer-edit-note"
              />
            </div>

            <div className="flex items-center justify-between rounded-xl border border-border/60 px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm">Recurring commitment</div>
                <div className="text-[11px] text-muted-foreground">Toggle if this repeats every cycle</div>
              </div>
              <Switch
                checked={draft.recurring}
                onCheckedChange={(v) => setDraft({ ...draft, recurring: v })}
                data-testid="transaction-drawer-edit-recurring"
              />
            </div>

            {onOpenAdvancedEditor && (
              <button
                type="button"
                onClick={onOpenAdvancedEditor}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                data-testid="transaction-drawer-open-advanced"
              >
                Advanced editor…
              </button>
            )}
          </div>

          {/* Footer actions */}
          <div className="flex items-center gap-2 pt-3 border-t border-border/40">
            <Button
              onClick={handleSave}
              disabled={saving || !isDirty}
              className="rounded-xl flex-1"
              data-testid="transaction-drawer-save-button"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            <Button
              onClick={onMarkReviewed}
              variant="outline"
              className="rounded-xl"
              data-testid="transaction-drawer-mark-reviewed-button"
              aria-label="Mark reviewed"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              onClick={() => setConfirmDelete(true)}
              variant="outline"
              className="rounded-xl text-destructive hover:text-destructive"
              data-testid="transaction-drawer-delete-button"
              aria-label="Delete transaction"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent data-testid="transaction-drawer-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this transaction?</AlertDialogTitle>
              <AlertDialogDescription>
                It will be soft-deleted. You can restore it later from SuperAdmin → Cleanup if needed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep it</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="transaction-drawer-delete-confirm-action"
              >
                {deleting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}
